/**
 * Browser localStorage keys for screener UI persistence.
 * State is scoped per authenticated user to avoid bleed on shared browsers.
 */

/** Legacy global key (pre user-scoping) */
export const LEGACY_SCREENER_STATE_KEY = "screener-state";

/** Prefix for per-user screener state buckets */
export const SCREENER_STATE_KEY_PREFIX = "screener-state:";

/** Legacy global list-view associations key */
export const LEGACY_SCREENER_LIST_VIEW_ASSOCIATIONS_KEY =
  "screener-list-view-associations";

/** Prefix for per-user list-view associations */
export const SCREENER_LIST_VIEW_ASSOCIATIONS_KEY_PREFIX =
  "screener-list-view-associations:";

/** Legacy global default list key */
export const LEGACY_SCREENER_DEFAULT_LIST_KEY = "screener-default-list";

/** Prefix for per-user default list preference */
export const SCREENER_DEFAULT_LIST_KEY_PREFIX = "screener-default-list:";

/** Prefix for per-user saved filter presets (Add Filter modal) */
export const SCREENER_SAVED_FILTER_PRESETS_KEY_PREFIX =
  "screener-saved-filter-presets:";

export function getScreenerStateStorageKey(userId: string): string {
  return `${SCREENER_STATE_KEY_PREFIX}${userId}`;
}

export function getScreenerListViewAssociationsStorageKey(
  userId: string,
): string {
  return `${SCREENER_LIST_VIEW_ASSOCIATIONS_KEY_PREFIX}${userId}`;
}

export function getScreenerDefaultListStorageKey(userId: string): string {
  return `${SCREENER_DEFAULT_LIST_KEY_PREFIX}${userId}`;
}

export function getScreenerSavedFilterPresetsStorageKey(
  userId: string,
  companyId?: string | null,
): string {
  const scope = companyId ? `${userId}:${companyId}` : userId;
  return `${SCREENER_SAVED_FILTER_PRESETS_KEY_PREFIX}${scope}`;
}

export const SCREENER_SAVED_FILTER_PRESETS_MIGRATED_KEY_PREFIX =
  "screener-saved-filter-presets-migrated:";

export function getScreenerSavedFilterPresetsMigratedKey(
  userId: string,
  companyId: string,
): string {
  return `${SCREENER_SAVED_FILTER_PRESETS_MIGRATED_KEY_PREFIX}${userId}:${companyId}`;
}

export function readScreenerSavedFilterPresets<T>(
  userId: string,
  companyId?: string | null,
): T[] {
  if (typeof window === "undefined") return [];
  try {
    const scopedKey = getScreenerSavedFilterPresetsStorageKey(userId, companyId);
    let raw = localStorage.getItem(scopedKey);
    if (!raw && companyId) {
      raw = localStorage.getItem(getScreenerSavedFilterPresetsStorageKey(userId));
    }
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

export function writeScreenerSavedFilterPresets<T>(
  userId: string,
  presets: T[],
  companyId?: string | null,
): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(
      getScreenerSavedFilterPresetsStorageKey(userId, companyId),
      JSON.stringify(presets),
    );
  } catch {
    // ignore
  }
}

export function clearScreenerStateForUser(userId: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(getScreenerStateStorageKey(userId));
    localStorage.removeItem(getScreenerListViewAssociationsStorageKey(userId));
    localStorage.removeItem(getScreenerDefaultListStorageKey(userId));
    localStorage.removeItem(LEGACY_SCREENER_STATE_KEY);
    localStorage.removeItem(LEGACY_SCREENER_LIST_VIEW_ASSOCIATIONS_KEY);
    localStorage.removeItem(LEGACY_SCREENER_DEFAULT_LIST_KEY);
  } catch {
    // ignore
  }
}

export function readScreenerListViewAssociations(
  userId: string,
): Record<string, string> {
  if (typeof window === "undefined") return {};
  try {
    const scoped = localStorage.getItem(
      getScreenerListViewAssociationsStorageKey(userId),
    );
    if (scoped) {
      return JSON.parse(scoped) as Record<string, string>;
    }
    const legacy = localStorage.getItem(LEGACY_SCREENER_LIST_VIEW_ASSOCIATIONS_KEY);
    return legacy ? (JSON.parse(legacy) as Record<string, string>) : {};
  } catch {
    return {};
  }
}

export function writeScreenerListViewAssociations(
  userId: string,
  associations: Record<string, string>,
): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(
      getScreenerListViewAssociationsStorageKey(userId),
      JSON.stringify(associations),
    );
    localStorage.removeItem(LEGACY_SCREENER_LIST_VIEW_ASSOCIATIONS_KEY);
  } catch {
    // ignore
  }
}

export function readScreenerDefaultListId(userId: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return (
      localStorage.getItem(getScreenerDefaultListStorageKey(userId)) ??
      localStorage.getItem(LEGACY_SCREENER_DEFAULT_LIST_KEY)
    );
  } catch {
    return null;
  }
}

export function writeScreenerDefaultListId(userId: string, listId: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(getScreenerDefaultListStorageKey(userId), listId);
    localStorage.removeItem(LEGACY_SCREENER_DEFAULT_LIST_KEY);
  } catch {
    // ignore
  }
}
